import type { JSX } from "solid-js";
import { render } from "solid-js/web";
import { game, type ShowPageData } from "~/js/game";
import { Pages } from "~/js/pages";
import type { ContainerItem, Item, KeyItem, MasterSkinItem, PageId, SkinItem, StickerItem } from "~/types";

export class SeedRandom {
	#modulusH: number = 4294967087;
	#multiplierH: number = 65539;
	#stateH: number = 0;

	#modulusA: number = 4294965887;
	#multiplierA: number = 65537;
	#stateA: number = 0;

	constructor(initialSeed: number = Date.now()) {
		this.#stateH = (initialSeed % (this.#modulusH - 1)) + 1;
		this.#stateA = (initialSeed % (this.#modulusA - 1)) + 1;

		this.skip(7);
	}

	next(): number {
		this.skip(1);

		const combinedResult = (this.#stateH + this.#stateA) % (this.#modulusA - 1);
		return combinedResult / (this.#modulusA - 1);
	}

	skip(numberOfSteps: number) {
		for (let s = 0; s < numberOfSteps; s++) {
			this.#stateH = (this.#stateH * this.#multiplierH) % this.#modulusH;
			this.#stateA = (this.#stateA * this.#multiplierA) % this.#modulusA;
		}
	}
}

export function isMasterItem(item: Item): item is MasterSkinItem {
	return item.type === "skin" && "masterItem" in item && item.masterItem;
}

export function isSkinItem(item: Item): item is SkinItem {
	return item.type === "skin" && !("masterItem" in item);
}

export function isStickerItem(item: Item): item is StickerItem {
	return item.type === "sticker";
}

export function isContainerItem(item: Item): item is ContainerItem {
	return item.type === "case" || item.type === "capsule";
}

export function isKeyItem(item: Item): item is KeyItem {
	return item.type === "key";
}

export function animateWithClass(element: HTMLElement, className: string, animationName?: string, callback?: () => void) {
	if (element.classList.contains(className)) return;
	element.classList.add(className);

	function handleAnimationEnd(this: HTMLElement, e: AnimationEvent) {
		if (e.animationName !== (animationName || className)) return;
		this.classList.remove(className);
		this.removeEventListener("animationend", handleAnimationEnd);
		if (callback) callback();
	}

	element.addEventListener("animationend", handleAnimationEnd);
}
export function createPage(element: () => JSX.Element, pageName: PageId, onshow?: (p: ShowPageData) => void) {
	const pageRoot = Pages.createPage(pageName);

	let dispose: (() => void) | null = null;
	game.on("showPage", (p) => {
		if (p.direct) return; // direct is a shitty way to get reactivity working without a framework, don't need it
		if (onshow) onshow(p);
		if (p.page === pageName) {
			if (!dispose) {
				dispose = render(element, pageRoot);
				console.log(`Rendered page: ${pageName}`);
			}
		} else if (p.page.length > 0) {
			if (dispose) {
				dispose();
				dispose = null;

				console.log(`Disposed page: ${pageName}`);
			}
		}
	});
	return pageRoot;
}

class UniformRandomStream {
	// matches IUniformRandomStream from the Source SDK
	static #NTAB = 32;
	static #IA = 16807;
	static #IM = 2147483647; // 0x7fffffff
	static #IQ = 127773;
	static #IR = 2836;
	static #NDIV = Math.trunc(1 + (UniformRandomStream.#IM - 1) / UniformRandomStream.#NTAB);
	static #MAX_RANDOM_RANGE = 0x7fffffff;
	static #AM = Math.fround(1.0 / UniformRandomStream.#IM);
	static #EPS = Math.fround(1.2e-7);
	static #RNMX = Math.fround(1.0 - UniformRandomStream.#EPS);

	private view: DataView;
	private iv: Int32Array;
	static fromCrypto() {
		const randomArray = new Int32Array(1);
		const seed = crypto.getRandomValues(randomArray)[0];
		return new UniformRandomStream(seed);
	}
	constructor(seed = 0) {
		// Layout: [mIdum:int32, mIy:int32, mIv[NTAB]:int32]
		const buffer = new ArrayBuffer((2 + UniformRandomStream.#NTAB) * 4); // 130 bytes
		this.view = new DataView(buffer);
		this.iv = new Int32Array(buffer, 8, UniformRandomStream.#NTAB);
		this.setSeed(seed);
	}

	private get mIdum() {
		return this.view.getInt32(0, true);
	}

	private set mIdum(v: number) {
		this.view.setInt32(0, v | 0, true);
	}

	private get mIy() {
		return this.view.getInt32(4, true);
	}

	private set mIy(v: number) {
		this.view.setInt32(4, v | 0, true);
	}

	setSeed(iSeed: number) {
		this.mIdum = iSeed | 0;
		if (iSeed >= 0) {
			this.mIdum = -iSeed | 0;
		}
		this.mIy = 0;
	}
	// generates a random integer in the range [0, 2^31-1]
	generateRandomNumber() {
		let j: number;
		let k: number;

		if (this.mIdum <= 0 || this.mIy === 0) {
			if (-this.mIdum < 1) {
				this.mIdum = 1;
			} else {
				this.mIdum = -this.mIdum;
			}

			for (j = UniformRandomStream.#NTAB + 7; j >= 0; j--) {
				k = Math.trunc(this.mIdum / UniformRandomStream.#IQ);
				this.mIdum = UniformRandomStream.#IA * (this.mIdum - k * UniformRandomStream.#IQ) - UniformRandomStream.#IR * k;
				if (this.mIdum < 0) {
					this.mIdum += UniformRandomStream.#IM;
				}
				if (j < UniformRandomStream.#NTAB) {
					this.iv[j] = this.mIdum;
				}
			}
			this.mIy = this.iv[0]!;
		}

		k = Math.trunc(this.mIdum / UniformRandomStream.#IQ);
		this.mIdum = UniformRandomStream.#IA * (this.mIdum - k * UniformRandomStream.#IQ) - UniformRandomStream.#IR * k;
		if (this.mIdum < 0) {
			this.mIdum += UniformRandomStream.#IM;
		}

		j = Math.trunc(this.mIy / UniformRandomStream.#NDIV);
		this.mIy = this.iv[j]!;
		this.iv[j] = this.mIdum;

		return this.mIy;
	}

	randomFloat(flLow: number, flHigh: number) {
		let fl = Math.fround(UniformRandomStream.#AM * this.generateRandomNumber()); // in [0,1)
		if (fl > UniformRandomStream.#RNMX) {
			fl = UniformRandomStream.#RNMX;
		}
		return Math.fround(Math.fround(fl * Math.fround(flHigh - flLow)) + Math.fround(flLow)); // in [low,high) quantized to f32
	}

	randomFloatExp(flMinVal: number, flMaxVal: number, flExponent: number) {
		let fl = Math.fround(UniformRandomStream.#AM * this.generateRandomNumber()); // in [0,1)
		if (fl > UniformRandomStream.#RNMX) {
			fl = UniformRandomStream.#RNMX;
		}
		if (flExponent !== 1.0) {
			fl = Math.fround(fl ** flExponent);
		}
		return Math.fround(Math.fround(fl * Math.fround(flMaxVal - flMinVal)) + Math.fround(flMinVal)); // in [low,high)
	}
	// generates a random integer in the range [iLow, iHigh]
	randomInt(iLow: number, iHigh: number) {
		const x = (iHigh | 0) - (iLow | 0) + 1;
		if (x <= 1 || UniformRandomStream.#MAX_RANDOM_RANGE < x - 1) {
			return iLow | 0;
		}

		const maxAcceptable = UniformRandomStream.#MAX_RANDOM_RANGE - ((UniformRandomStream.#MAX_RANDOM_RANGE + 1) % x);
		let n: number;
		for (;;) {
			n = this.generateRandomNumber();
			if (n <= maxAcceptable) {
				break;
			}
		}
		return (iLow | 0) + (n % x);
	}
}
export const randomStream = UniformRandomStream.fromCrypto();
